<template>
  <v-form
    ref="form"
    v-model="valid"
    lazy-validation
  > 
    <div v-if="config.name !== undefined || !id" class="mb-5">
      <v-row><h5 class="text--disabled">Input</h5></v-row>
      <v-row class="mb-3"><v-divider /></v-row>
      <v-row class="mb-3">
        <v-col cols="12">
          <v-text-field
            v-model="config.name"
            label="Input Name"
            @input="configChanged"
            :disabled="!canEdit"
            outlined
            hide-details
          ></v-text-field>
        </v-col>
      </v-row>
    </div>
    
    <v-row><h5 class="text--disabled">Input Settings</h5></v-row>
    <v-row class="mb-3"><v-divider /></v-row>
    <v-row>
      <v-col cols="5">
        <v-select
          v-model="config.video_rotation"
          :items="generateSelectItems(video_rotationValues, 'daemon.encoders.video.source_rotations', {0: 'ROTATE_0', 90: 'ROTATE_90', 180: 'ROTATE_180', 270: 'ROTATE_270'})"
          label="Video Input Rotation"
          @change="configChanged"
          :disabled="!canEdit"
          outlined
          hide-details
        ></v-select>
      </v-col>
    </v-row>
    
    <v-row v-if="config.enable_persistent_input !== undefined" class="mb-3">
      <v-col cols="4">
        <v-checkbox
          v-model="config.enable_persistent_input"
          label="Persistent Input"
          @change="configChanged"
          :disabled="true || !canEdit || !hasFeature('daemon.input.video.failover')"
          hide-details="auto"
          class="mt-0"
        ></v-checkbox>
      </v-col>
      <v-col cols="3">
        <v-checkbox
          v-model="config.force_av_mute"
          label="A/V Mute"
          @change="configChanged"
          :disabled="true || !canEdit"
          hide-details="auto"
          class="mt-0"
        ></v-checkbox>
      </v-col>
    </v-row>
    
    <v-row><h5 class="text--disabled">Graphic Overlay</h5></v-row>
    <v-row class=""><v-divider /></v-row>
    
    <div v-if="config.cef !== undefined && config.cef.enable !== undefined && device.product_name == 'edgecaster_max'">
      <v-row class="mt-3">
        <v-col cols="12">
          <v-checkbox
            v-model="config.cef.enable"
            label="Enable Dynamic Graphic Overlay"
            @change="configChanged"
            :disabled="!canEdit || !hasFeature('daemon.input.video.cef')"
            hide-details="auto"
            class="mt-2 mb-3"
          ></v-checkbox>
        </v-col>
      </v-row>
      <div v-if="config.cef.enable">
        <v-row class="mb-3">
          <v-col cols="12">
            <v-text-field
              v-model="config.cef.url"
              label="Overlay Content URL"
              @input="configChanged"
              :disabled="!canEdit || !hasFeature('daemon.input.video.cef')"
              outlined
              hide-details="auto"
            ></v-text-field>
          </v-col>
        </v-row>
      </div>
    </div>
    
    <v-row>
      <v-col cols="12">
        <v-checkbox
          v-model="config.graphic_overlay.enable"
          label="Enable Static Graphic Overlay"
          @change="configChanged"
          :disabled="!canEdit || !hasFeature('daemon.encoders.video.overlay.enabled')"
          hide-details="auto"
          class="mt-0"
        ></v-checkbox>
      </v-col>
    </v-row>
    <div v-if="config.graphic_overlay.enable">
      <v-row>
        <v-col cols="12">
          <v-alert
            type="info"
            text
            icon="mdi-lock-outline"
            class="mt-3"
          >
            Graphic Overlay images must be managed directly on the device at this time.
          </v-alert>
        </v-col>
      </v-row>
      <v-row class="mb-3">
        <v-col cols="12">
          <v-select
            v-model="config.graphic_overlay.preset_location"
            :items="generateSelectItems(graphic_overlay_preset_locationValues)"
            label="Overlay Position"
            @change="configChanged"
            :disabled="!canEdit || !hasFeature('daemon.encoders.video.overlay.enabled')"
            hide-details="auto"
            outlined
          ></v-select>
        </v-col>
      </v-row>
      
      <div v-if="config.graphic_overlay.preset_location == 'MANUAL'">
        <v-row class="mb-3">
          <v-col cols="6">
            <v-text-field
              v-model.number="config.graphic_overlay.x_position"
              label="Image X Position %"
              @input="configChanged"
              :disabled="!canEdit || !hasFeature('daemon.encoders.video.overlay.enabled')"
              type="number"
              :rules="$validations.percent"
              hide-details="auto"
              outlined
            ></v-text-field>
          </v-col>
          <v-col cols="6">
            <v-text-field
              v-model.number="config.graphic_overlay.y_position"
              label="Image Y Position %"
              @input="configChanged"
              :disabled="!canEdit || !hasFeature('daemon.encoders.video.overlay.enabled')"
              type="number"
              :rules="$validations.percent"
              hide-details="auto"
              outlined
            ></v-text-field>
          </v-col>
        </v-row>
      </div>
      
      <div v-if="config.graphic_overlay.preset_location != 'FULLSCREEN' && config.graphic_overlay.preset_location != 'LOWER_THIRD'">
        <v-row class="mt-3">
          <h5 class="text--disabled">Graphic Overlay Image Scaling</h5>
          <v-spacer/>
          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                text
                small
                v-bind="attrs"
                v-on="on"
              >
                <v-icon small>question_mark</v-icon>
              </v-btn>
            </template>
            <span style="line-height: 1.5em">
              When 'Lock Aspect Ratio' is configured to width or height scaling will be done automatically to keep the original images aspect ratio.<br/>
              Image Width % and Image Height % represent the % of input stream covered by the overlay image.<br/>
              Example: With an output resolution 1920x1080p when the Image Width % is set to 100% the overlay image will be 1920 pixels wide.
            </span>
          </v-tooltip>
        </v-row>
        <v-row class="mb-3"><v-divider /></v-row>
        <v-row class="mb-3">
          <v-col cols="12">
            <v-select
              v-model="config.graphic_overlay.aspect_ratio_lock"
              :items="generateSelectItems(graphic_overlay_aspect_ratio_lockValues)"
              label="Lock Aspect Ratio"
              @change="configChanged"
              :disabled="!canEdit || !hasFeature('daemon.encoders.video.overlay.enabled')"
              hide-details="auto"
              outlined
            ></v-select>
          </v-col>
        </v-row>
        <v-row class="mb-3">
          <v-col cols="6" v-if="config.graphic_overlay.aspect_ratio_lock == 'NO_LOCK' || config.graphic_overlay.aspect_ratio_lock == 'LOCK_TO_WIDTH'">
            <v-text-field
              v-model.number="config.graphic_overlay.width"
              label="Image Width %"
              @input="configChanged"
              :disabled="!canEdit || !hasFeature('daemon.encoders.video.overlay.enabled')"
              type="number"
              :rules="$validations.percent"
              hide-details="auto"
              outlined
            ></v-text-field>
          </v-col>
          <v-col cols="6" v-if="config.graphic_overlay.aspect_ratio_lock == 'NO_LOCK' || config.graphic_overlay.aspect_ratio_lock == 'LOCK_TO_HEIGHT'">
            <v-text-field
              v-model.number="config.graphic_overlay.height"
              label="Image Height %"
              @input="configChanged"
              :disabled="!canEdit || !hasFeature('daemon.encoders.video.overlay.enabled')"
              type="number"
              :rules="$validations.percent"
              hide-details="auto"
              outlined
            ></v-text-field>
          </v-col>
        </v-row>
      </div>
      <div v-if="config.graphic_overlay.preset_location == 'LOWER_THIRD'">
        <v-row><h5 class="text--disabled">Graphic Overlay Image Scaling</h5></v-row>
        <v-row class="mb-3"><v-divider /></v-row>
        <v-row class="mb-3">
          <v-col cols="3">
            <v-text-field
              v-model.number="config.graphic_overlay.height"
              label="Image Height %"
              @input="configChanged"
              :disabled="!canEdit || !hasFeature('daemon.encoders.video.overlay.enabled')"
              type="number"
              :rules="$validations.percent"
              hide-details="auto"
              outlined
            ></v-text-field>
          </v-col>
        </v-row>
      </div>
    </div>
    
    <div v-if="id && !isNew && validInput && showPreview && hasFeature('daemon.encoders.video.input_preview.enabled')" class="mt-5">
      <v-row><h5 class="text--disabled">AV Input Preview</h5></v-row>
      <v-row class="mb-3"><v-divider /></v-row>
      <v-row class="mb-3">
        <v-col v-if="thumbnailEnabled" cols="12">
          <input-preview :device_guid="device_guid" :input_id="id" :input_type="input.type" />
        </v-col>
        <v-col v-else cols="12">
          <v-alert
            type="info"
            text
            class="mt-3"
          >
            To utilize input preview, please ensure a running thumbnail output for this input.
          </v-alert>
        </v-col>
      </v-row>
    </div>
    
    <div v-if="id && !isNew && shadowDebug" class="mt-5">
      <v-row><h5 class="text--disabled">Input Details</h5></v-row>
      <v-row class="mb-3"><v-divider /></v-row>
      <v-row class="mb-3">
        <v-col cols="12">
          <pre style="inline-size: 640px; white-space: pre-wrap;">{{ input }}</pre>
        </v-col>
      </v-row>
    </div>
    
    <div v-if="id && !isNew && canEdit" class="text-right">
      <v-btn
        class="ma-1 d-none d-md-inline"
        x-small
        outlined
        @click="deleteInput()"
      >
        Delete Input
      </v-btn>
    </div>
  </v-form>
</template>
<script>
  import { mapGetters } from 'vuex'
  import ShadowMixins from '../mixins/mixins.js'
  import InputPreview from '../../../components/InputPreview.vue'

  export default {
    name: 'GStreamerInputDocument',
    
    props: ['input', 'documents', 'device_guid', 'canEdit', 'isNew'],
    
    components: {
      InputPreview
    },
    
    mixins:[ShadowMixins],
    
    data() {
      return {
        valid: true,
        
        showPreview: true,
        
        id: '',
        audio_input_detected: false,
        video_input_format: '',
        
        video_rotationValues: [
          'ROTATE_0',
          'ROTATE_90',
          'ROTATE_180',
          'ROTATE_270',
        ],
        
        graphic_overlay_aspect_ratio_lockValues: [
          'NO_LOCK',
          'LOCK_TO_WIDTH',
          'LOCK_TO_HEIGHT',
        ],
        
        graphic_overlay_preset_locationValues: [
          'MANUAL',
          'FULLSCREEN',
          'LOWER_THIRD',
          'TOP_LEFT',
          'BOTTOM_LEFT',
          'TOP_CENTER',
          'CENTER',
          'BOTTOM_CENTER',
          'TOP_RIGHT',
          'BOTTOM_RIGHT'
        ],
        
        config: {
          video_rotation: 'ROTATE_0',
          
          //enable_persistent_input: false, # this property will get set if its available in the shadow
          //force_av_mute: false, # this property will get set if its available in the shadow
          
          //cef: { # this property will get set if its available in the shadow
          //  'enable': false,
          //  'url': ''
          //},
          
          graphic_overlay: {
            'enable': false,
            'x_position': 0,
            'y_position': 0,
            'width': 100,
            'height': 100,
            'aspect_ratio_lock': 'NO_LOCK',
            'preset_location': 'MANUAL'
          },

        }
      }
    },
    
    watch: {
      input() {
        this.updateValues()
      },
    },
    
    computed: {
      ...mapGetters('userPreferences', ['shadowDebug']),
      
      device() {
        return this.$devices.getDevice(this.device_guid)
      },
      
      inputIndex() {
        for (var index in this.documents) {
          if (this.documents[index].id == this.input.id) {
            return index
          }
        }
        return -1
      },
      
      thumbnailEnabled() {
        return this.device.thumbnailEnabled(this.inputIndex)
      },
      
      validInput() {
        return (this.video_input_format == 'FORMAT_UNDEFINED') ? false : true
      }
    },
    
    mounted() {
      this.updateValues()
    },
    
    methods: {
      configChanged() {
        var validationErrors = false
        
        // check for validation errors
        if (!this.$refs.form.validate()) {
          console.log('GStreamerInputDocument ' + this.device_guid + ' validation error', this.config)
          validationErrors = true
        }
        
        console.log('GStreamerInputDocument ' + this.device_guid + ' configChanged', this.config)
        this.$emit('configUpdate', this.config, validationErrors)
      },
      
      updateValues() {
        if (this.input && this.input.config) {
          Object.keys(this.input).map((key) => {
            this[key] = this.input[key]
          })
          
          // htmlDecode name properties
          if (this.config.name !== undefined) {
            this.config.name = this.$helpers.htmlDecode(this.config.name)
          }
        }
        
        // populate transient values
        if (this.isNew && this.device.product_name == 'edgecaster_max') {
          console.log('GStreamerInputDocument populating transient properties')
          if (!this.config.enable_persistent_input) {
            this.$set(this.config, 'enable_persistent_input', false)
          }
          if (!this.config.force_av_mute) {
            this.$set(this.config, 'force_av_mute', false)
          }
        }
        
        if (this.isNew && this.device.product_name == 'edgecaster_max') {
          console.log('GStreamerInputDocument populating CEF properties')
          if (!this.config.cef) {
            this.$set(this.config, 'cef', {})
          }
          
          if (!this.config.cef.enable) {
            this.$set(this.config.cef, 'enable', false)
          }
          
          if (!this.config.cef.url) {
            this.$set(this.config.cef, 'url', '')
          }
        }
        
        this.configChanged()
      },
      
      deleteInput() {
        console.log('GStreamerInputDocument ' + this.device_guid + ' deleteInput')
        this.$emit('deleteInput')
      }
    }
  }
</script>